// src/components/email/ThreadTimeline.jsx
import React from 'react';
import { MessageCircle, CheckCircle, Clock, ArrowUpRight, Calendar } from 'lucide-react';

const ThreadTimeline = ({ threadActivity, currentEmailId }) => {
  if (!threadActivity || !threadActivity.hasReplies) {
    return null;
  }

  const {
    totalMessages,
    verifiedMessages,
    participants,
    originalMessage,
    replies,
    threadDuration,
    currentEmail,
    verifiedEmail // This contains the currently verified email information
  } = threadActivity;

  // Format timestamps into relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHours = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffDays > 7) {
        return formatDate(dateString);
      } else if (diffDays > 1) {
        return `${diffDays} days ago`;
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffHours > 1) {
        return `${diffHours} hours ago`;
      } else if (diffMin > 1) {
        return `${diffMin} minutes ago`;
      } else {
        return 'Just now';
      }
    } catch {
      return formatDate(dateString);
    }
  };

  // Format dates with more details
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Check if an email is verified in the thread
  const isEmailVerified = (emailId) => {
    // If this is the email being verified right now
    if (verifiedEmail && verifiedEmail.id === emailId) {
      return true;
    }
    
    // Original message verification
    if (originalMessage.id === emailId) {
      return originalMessage.isVerified;
    }
    
    // Reply verification
    const reply = replies.find(r => r.id === emailId);
    return reply ? reply.isVerified : false;
  };

  // Timeline item for each message in the thread
  const TimelineItem = ({ message, date, isVerified, isCurrent, isOriginal, sender, emailId }) => (
    <div className={`relative pl-4 pb-5 ${isCurrent ? 'mb-4' : 'mb-1'}`}>
      {/* Line connector */}
      <div className="absolute left-0 top-3 bottom-0 w-px bg-ebony/20"></div>
      
      {/* Message dot */}
      <div className={`absolute left-[-5px] top-3 w-[10px] h-[10px] rounded-full ${
        isCurrent ? 'bg-violet' : isOriginal ? 'bg-violet/70' : isVerified ? 'bg-spring-green' : 'bg-ebony/30'
      }`}></div>
      
      {/* Message content */}
      <div className={`pl-3 pt-0 ${isCurrent ? 'bg-violet/5 p-2 rounded-xl border border-violet/20' : ''}`}>
        <div className="flex items-center justify-between">
          <span className={`text-sm font-medium ${isCurrent ? 'text-violet' : 'text-midnight'} ${isCurrent || isOriginal ? 'font-heading' : ''}`}>
            {isOriginal ? 'Original message' : isCurrent ? 'Current message' : 'Reply'}
            {isCurrent && <ArrowUpRight className="inline-block h-3 w-3 ml-1 text-violet" />}
          </span>
          <span className="text-xs text-ebony">{formatRelativeTime(date)}</span>
        </div>
        
        {/* Sender info */}
        {sender && (
          <div className="text-xs text-ebony mt-1">
            From: {sender.name} ({sender.email.split('@')[1]})
          </div>
        )}
        
        {/* Verification status (subtle indicator) */}
        <div className="flex items-center text-xs mt-1">
          {isVerified || (verifiedEmail && verifiedEmail.id === emailId) ? (
            <span className="text-spring-green flex items-center">
              <CheckCircle className="h-3 w-3 mr-1" />
              Verified
            </span>
          ) : (
            <span className="text-ebony/50">Unverified</span>
          )}
        </div>
      </div>
    </div>
  );

  // Find the current email's data
  const currentEmailData = currentEmail?.isOriginal 
    ? originalMessage 
    : replies.find(r => r.id === currentEmail?.id);

  return (
    <div className="bg-white rounded-xl border border-ebony/20 shadow-md p-4 space-y-4">
      {/* Thread summary header */}
      <div className="flex items-center">
        <MessageCircle className="h-5 w-5 text-violet mr-2 flex-shrink-0" />
        <div>
          <h4 className="font-medium text-midnight font-heading">
            {totalMessages > 1 
              ? `Email Thread (${totalMessages} Messages)`
              : 'Email Thread'}
          </h4>
          <div className="text-sm text-ebony flex flex-wrap gap-3 mt-1">
            <span className="flex items-center">
              <Calendar className="h-3.5 w-3.5 text-violet mr-1" />
              {formatDate(originalMessage.sentAt)}
            </span>
            
            {threadDuration && (
              <span className="flex items-center">
                <Clock className="h-3.5 w-3.5 text-violet mr-1" />
                {threadDuration}
              </span>
            )}
            
            <span className="flex items-center">
              <CheckCircle className="h-3.5 w-3.5 text-spring-green mr-1" />
              {/* Calculate verified count including the current verification */}
              {verifiedEmail ? verifiedMessages + 1 : verifiedMessages} of {totalMessages} verified
            </span>
          </div>
        </div>
      </div>

      {/* Simplified participants section */}
      {participants.length > 1 && (
        <div className="px-3 py-2 bg-old-lace/30 rounded-lg">
          <span className="text-xs font-medium text-midnight font-heading">
            Participants: 
          </span>
          <div className="flex flex-wrap gap-2 mt-1">
            {participants.map((participant) => (
              <div key={participant.email} className="inline-flex items-center text-xs bg-white rounded-pill px-2 py-1 border border-ebony/20">
                <span className="w-4 h-4 rounded-full bg-violet/10 flex items-center justify-center text-[10px] text-violet mr-1">
                  {participant.name.charAt(0).toUpperCase()}
                </span>
                <span className="text-ebony">{participant.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline section */}
      <div className="pt-2">
        <div className="mt-2">
          {/* Original message */}
          <TimelineItem 
            message={originalMessage.messageId}
            date={originalMessage.sentAt}
            isVerified={isEmailVerified(originalMessage.id)}
            isCurrent={currentEmail?.isOriginal}
            isOriginal={true}
            sender={originalMessage.sender}
            emailId={originalMessage.id}
          />
          
          {/* Replies in chronological order */}
          {replies.map((reply) => (
            <TimelineItem 
              key={reply.id}
              message={reply.messageId}
              date={reply.sentAt}
              isVerified={isEmailVerified(reply.id)}
              isCurrent={reply.id === currentEmail?.id}
              isOriginal={false}
              sender={reply.sender}
              emailId={reply.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ThreadTimeline;